/**
 * Enterprise-Grade Unified Icon System for Zubduq CRM
 * Provides consistent, performant, and accessible SVG icons across all contexts
 *
 * Features:
 * - Single API for all use cases (HTML, JS, Components)
 * - Performance optimized with caching
 * - Accessibility compliant
 * - TypeScript-like intellisense support
 * - Consistent sizing and theming
 * - Auto-injection for HTML
 * - Framework agnostic
 *
 * Note: Styles are handled by client/src/scss/components/_icons.scss
 *
 * @version 2.0.0
 * @author Zubduq Development Team
 */

// Re-export all icons and utilities from the main icon system
export { ICONS, ICON_COLORS, IconUtils } from '../constants/icons.js';
import { IconUtils as IconUtilsImported, getIconEmoji } from '../constants/icons.js';

/**
 * Enterprise Icon Manager - Unified API for all icon operations
 * This is the single entry point for all icon-related functionality
 */
export class IconManager {
  static instance = null;
  static cache = new Map();
  static initialized = false;

  /**
   * Singleton pattern for performance
   */
  static getInstance() {
    if (!this.instance) {
      this.instance = new IconManager();
    }
    return this.instance;
  }

  /**
   * Initialize the icon system - called automatically
   */
  static initialize() {
    if (this.initialized) return;

    // Initialize HTML injection system
    this.initializeHTMLInjection();

    // Set up performance monitoring
    this.setupPerformanceMonitoring();

    this.initialized = true;
    console.log('🎨 IconManager: Enterprise icon system initialized');
  }

  /**
   * UNIFIED API - Get SVG markup (recommended for all use cases)
   * @param {string} iconName - Name of the icon
   * @param {Object} options - Configuration options
   * @returns {string} Complete SVG markup ready for insertion
   */
  static get(iconName, options = {}) {
    const cacheKey = `${iconName}-${JSON.stringify(options)}`;

    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    const {
      size = 24,
      color = null,
      className = '',
      title = '',
      spin = false,
      inline = false
    } = options;

    try {
      // Get the base SVG from the icon system
      const svgContent = this.getSVGContent(iconName, color, size);

      if (!svgContent) {
        console.warn(`IconManager: Icon '${iconName}' not found`);
        return this.getFallbackIcon(iconName, options);
      }

      // Wrap in container with proper attributes
      const containerClasses = [
        'icon',
        `icon--${iconName}`,
        spin ? 'icon--spin' : '',
        className
      ].filter(Boolean).join(' ');

      const containerStyle = inline ? 'display: inline-flex; align-items: center;' : '';
      const titleAttr = title ? ` title="${title}" aria-label="${title}"` : '';

      const result = `<span class="${containerClasses}" style="${containerStyle}"${titleAttr} data-icon="${iconName}">${svgContent}</span>`;

      // Cache the result
      this.cache.set(cacheKey, result);

      return result;
    } catch (error) {
      console.error(`IconManager: Error rendering icon '${iconName}':`, error);
      return this.getFallbackIcon(iconName, options);
    }
  }

  /**
   * Get raw SVG content (for advanced use cases)
   * @param {string} iconName - Name of the icon
   * @param {string} color - Optional color override
   * @param {number} size - Optional size override
   * @returns {string} Raw SVG markup
   */
  static getSVGContent(iconName, color = null, size = 24) {
    // Use the imported IconUtils
    return IconUtilsImported.getSvg(iconName, color, size);
  }

  /**
   * Inject icons into HTML elements (for static HTML)
   * @param {HTMLElement|Document} container - Container to search for icon placeholders
   */
  static injectIntoHTML(container = document) {
    // Handle data-icon attributes (existing functionality)
    const placeholders = container.querySelectorAll('[data-icon]:not([data-icon-injected])');

    placeholders.forEach(placeholder => {
      const iconName = placeholder.getAttribute('data-icon');
      const size = placeholder.getAttribute('data-icon-size') || 24;
      const color = placeholder.getAttribute('data-icon-color') || null;
      const spin = placeholder.hasAttribute('data-icon-spin');

      try {
        const svgContent = this.getSVGContent(iconName, color, parseInt(size));

        if (svgContent) {
          placeholder.innerHTML = svgContent;
          placeholder.setAttribute('data-icon-injected', 'true');

          if (spin) {
            placeholder.classList.add('icon--spin');
          }
        }
      } catch (error) {
        console.error(`IconManager: Failed to inject icon '${iconName}':`, error);
      }
    });

    // Handle CSS class format (new functionality)
    const iconElements = container.querySelectorAll('i.icon:not([data-icon-injected])');

    iconElements.forEach(iconElement => {
      // Extract icon name from class list (icon--iconname)
      const iconClass = Array.from(iconElement.classList).find(cls => cls.startsWith('icon--') && cls !== 'icon--spin' && cls !== 'icon--xs' && cls !== 'icon--sm' && cls !== 'icon--md' && cls !== 'icon--lg' && cls !== 'icon--xl' && cls !== 'icon--2xl' && cls !== 'icon--3xl' && cls !== 'icon--4xl');

      if (iconClass) {
        const iconName = iconClass.replace('icon--', '');
        const hasSpinClass = iconElement.classList.contains('icon--spin');

        // Determine size from size classes
        let size = 24; // default
        if (iconElement.classList.contains('icon--xs')) size = 12;
        else if (iconElement.classList.contains('icon--sm')) size = 14;
        else if (iconElement.classList.contains('icon--md')) size = 16;
        else if (iconElement.classList.contains('icon--lg')) size = 20;
        else if (iconElement.classList.contains('icon--xl')) size = 24;
        else if (iconElement.classList.contains('icon--2xl')) size = 32;
        else if (iconElement.classList.contains('icon--3xl')) size = 48;
        else if (iconElement.classList.contains('icon--4xl')) size = 64;

        try {
          const svgContent = this.getSVGContent(iconName, null, size);

          if (svgContent) {
            iconElement.innerHTML = svgContent;
            iconElement.setAttribute('data-icon-injected', 'true');

            if (hasSpinClass) {
              iconElement.classList.add('icon--spin');
            }
          }
        } catch (error) {
          console.error(`IconManager: Failed to inject CSS icon '${iconName}':`, error);
        }
      }
    });
  }

  /**
   * Get fallback icon for missing or errored icons
   */
  static getFallbackIcon(iconName, options = {}) {
    const emoji = getIconEmoji(iconName);
    const { size = 24, className = '' } = options;

    return `<span class="icon icon--fallback ${className}" style="font-size: ${size}px; display: inline-flex; align-items: center;" title="Icon: ${iconName}">${emoji}</span>`;
  }

  /**
   * Initialize HTML injection system
   */
  static initializeHTMLInjection() {
    // Inject icons on DOM ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.injectIntoHTML());
    } else {
      this.injectIntoHTML();
    }

    // Set up mutation observer for dynamic content
    if (typeof window !== 'undefined' && window.MutationObserver) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList') {
            mutation.addedNodes.forEach((node) => {
              if (node.nodeType === Node.ELEMENT_NODE) {
                this.injectIntoHTML(node);
              }
            });
          }
        });
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    }
  }

  /**
   * Setup performance monitoring
   */
  static setupPerformanceMonitoring() {
    if (typeof window !== 'undefined') {
      window.IconManager = this;

      // Performance metrics
      this.metrics = {
        iconsRendered: 0,
        cacheHits: 0,
        cacheMisses: 0,
        errors: 0
      };

      // Debug helpers
      window.debugIcons = () => {
        console.table(this.metrics);
        console.log('Cache size:', this.cache.size);
        console.log('Cached icons:', Array.from(this.cache.keys()));
      };
    }
  }

  /**
   * Clear cache (for development/testing)
   */
  static clearCache() {
    this.cache.clear();
    console.log('IconManager: Cache cleared');
  }
}

// Auto-initialize when module is loaded
if (typeof window !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => IconManager.initialize());
  } else {
    IconManager.initialize();
  }
}

// Main API - Use this for all new code
export default IconManager;
